import React, { useState, useEffect, useCallback } from 'react';
import CountryFlag, { countryFromRegion } from '../../../shared/components/CountryFlag';
import {
    ReactFlow,
    Background,
    Controls,
    MiniMap,
    useNodesState,
    useEdgesState,
    Handle,
    Position,
    MarkerType,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import dagre from 'dagre';
import { Card, Button, Space, Spin, message, Tag, Tooltip, Modal, Descriptions, Badge, Select } from 'antd';
import {
    ReloadOutlined,
    CloudOutlined,
    ClusterOutlined,
    GlobalOutlined,
    ExpandOutlined,
    CompressOutlined,
    ApartmentOutlined,
} from '@ant-design/icons';
import cdnService from '../../services/cdnService';
import L2Gate from './L2Gate';

// Dagre布局配置
const dagreGraph = new dagre.graphlib.Graph();
dagreGraph.setDefaultEdgeLabel(() => ({}));

const getLayoutedElements = (nodes, edges, direction = 'TB') => {
    dagreGraph.setGraph({ rankdir: direction, nodesep: 100, ranksep: 120 });

    nodes.forEach((node) => {
        dagreGraph.setNode(node.id, { width: node.width || 180, height: node.height || 60 });
    });

    edges.forEach((edge) => {
        dagreGraph.setEdge(edge.source, edge.target);
    });

    dagre.layout(dagreGraph);

    const layoutedNodes = nodes.map((node) => {
        const nodeWithPosition = dagreGraph.node(node.id);
        return {
            ...node,
            position: {
                x: nodeWithPosition.x - (node.width || 180) / 2,
                y: nodeWithPosition.y - (node.height || 60) / 2,
            },
        };
    });

    return { nodes: layoutedNodes, edges };
};

// 自定义节点: 节点分组
const NodeGroupNode = ({ data }) => {
    return (
        <div
            style={{
                padding: '14px 18px',
                borderRadius: 10,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                minWidth: 180,
                boxShadow: '0 4px 14px rgba(102, 126, 234, 0.4)',
            }}
        >
            <Handle type="source" position={Position.Bottom} style={{ background: '#555' }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <ApartmentOutlined style={{ fontSize: 22 }} />
                <div>
                    <div style={{ fontWeight: 600, fontSize: 15 }}>{data.label}</div>
                    <div style={{ fontSize: 11, opacity: 0.9 }}>{data.region || '节点分组'}</div>
                </div>
            </div>
            <div style={{ marginTop: 8, display: 'flex', gap: 8, fontSize: 11 }}>
                <span style={{ opacity: 0.8 }}>{data.l2Count} L2</span>
                <span style={{ opacity: 0.8 }}>{data.l1Count} L1</span>
            </div>
        </div>
    );
};

// 自定义节点: L2节点 (中枢节点)
const L2Node = ({ data }) => {
    const statusColor = data.status === 'active' ? '#52c41a' : '#ff4d4f';
    return (
        <div
            style={{
                padding: '12px 16px',
                borderRadius: 8,
                background: 'linear-gradient(135deg, #eb3349 0%, #f45c43 100%)',
                color: 'white',
                minWidth: 170,
                boxShadow: '0 4px 12px rgba(235, 51, 73, 0.4)',
                cursor: 'pointer',
            }}
            onClick={() => data.onClick && data.onClick(data.raw)}
        >
            <Handle type="target" position={Position.Top} style={{ background: '#555' }} />
            <Handle type="source" position={Position.Bottom} style={{ background: '#555' }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <CloudOutlined style={{ fontSize: 18 }} />
                <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{data.label}</div>
                    <div style={{ fontSize: 10, opacity: 0.9 }}>{data.ip}</div>
                </div>
                <div
                    style={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: statusColor,
                    }}
                />
            </div>
            <div style={{ marginTop: 6 }}>
                <Tag
                    style={{
                        fontSize: 10,
                        padding: '0 6px',
                        background: 'rgba(255,255,255,0.2)',
                        border: 'none',
                        color: 'white',
                    }}
                >
                    L2 中枢
                </Tag>
                {data.l2Port && (
                    <span style={{ fontSize: 10, marginLeft: 4, opacity: 0.8 }}>
                        :{data.l2Port}
                    </span>
                )}
            </div>
        </div>
    );
};

// 自定义节点: L1节点 (叶子节点)
const L1Node = ({ data }) => {
    const statusColor = data.status === 'active' ? '#52c41a' : '#ff4d4f';
    return (
        <div
            style={{
                padding: '10px 14px',
                borderRadius: 6,
                background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                color: 'white',
                minWidth: 150,
                boxShadow: '0 4px 12px rgba(17, 153, 142, 0.4)',
                cursor: 'pointer',
            }}
            onClick={() => data.onClick && data.onClick(data.raw)}
        >
            <Handle type="target" position={Position.Top} style={{ background: '#555' }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <GlobalOutlined style={{ fontSize: 16 }} />
                <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 12 }}>{data.label}</div>
                    <div style={{ fontSize: 10, opacity: 0.9 }}>{data.ip}</div>
                </div>
                <Tooltip title={data.status}>
                    <div
                        style={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            background: statusColor,
                        }}
                    />
                </Tooltip>
            </div>
            <div style={{ marginTop: 6 }}>
                <Tag
                    style={{
                        fontSize: 10,
                        padding: '0 6px',
                        background: 'rgba(255,255,255,0.2)',
                        border: 'none',
                        color: 'white',
                    }}
                >
                    L1 边缘
                </Tag>
            </div>
        </div>
    );
};

// 自定义节点: 未分组节点
const UngroupedNode = ({ data }) => {
    const statusColor = data.status === 'active' ? '#52c41a' : '#ff4d4f';
    return (
        <div
            style={{
                padding: '10px 14px',
                borderRadius: 6,
                background: 'linear-gradient(135deg, #636e72 0%, #b2bec3 100%)',
                color: 'white',
                minWidth: 150,
                boxShadow: '0 4px 12px rgba(99, 110, 114, 0.4)',
                cursor: 'pointer',
            }}
            onClick={() => data.onClick && data.onClick(data.raw)}
        >
            <Handle type="target" position={Position.Top} style={{ background: '#555' }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <CloudOutlined style={{ fontSize: 16 }} />
                <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 12 }}>{data.label}</div>
                    <div style={{ fontSize: 10, opacity: 0.9 }}>{data.ip}</div>
                </div>
                <Tooltip title={data.status}>
                    <div
                        style={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            background: statusColor,
                        }}
                    />
                </Tooltip>
            </div>
        </div>
    );
};

const nodeTypes = {
    nodeGroup: NodeGroupNode,
    l2Node: L2Node,
    l1Node: L1Node,
    ungroupedNode: UngroupedNode,
};

const Topology = () => {
    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);
    const [loading, setLoading] = useState(false);
    const [fullscreen, setFullscreen] = useState(false);
    const [selectedNode, setSelectedNode] = useState(null);
    const [detailModalVisible, setDetailModalVisible] = useState(false);
    const [viewMode, setViewMode] = useState('nodeGroups'); // nodeGroups | cdnLines

    // 原始数据
    const [rawData, setRawData] = useState({
        nodeGroups: [],
        cdnNodes: [],
        cdnGroups: [],
        cdnLines: [],
    });

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const [nodeGroupsRes, nodesRes, cdnGroupsRes, cdnLinesRes] = await Promise.all([
                cdnService.getNodeGroups(),
                cdnService.getNodes(),
                cdnService.getCdnGroups(),
                cdnService.getCdnLines(),
            ]);

            const nodeGroups = nodeGroupsRes.success ? nodeGroupsRes.data || [] : [];
            const cdnNodes = nodesRes.success ? nodesRes.data || [] : [];
            const cdnGroups = cdnGroupsRes.success ? cdnGroupsRes.data || [] : [];
            const cdnLines = cdnLinesRes.success ? cdnLinesRes.data || [] : [];

            setRawData({ nodeGroups, cdnNodes, cdnGroups, cdnLines });
            buildTopology(nodeGroups, cdnNodes, cdnGroups, cdnLines, viewMode);
        } catch (error) {
            message.error('加载数据失败: ' + error.message);
        } finally {
            setLoading(false);
        }
    }, [viewMode]);

    const buildTopology = useCallback(
        (nodeGroups, cdnNodes, cdnGroups, cdnLines, mode) => {
            const flowNodes = [];
            const flowEdges = [];

            if (mode === 'nodeGroups') {
                // 按节点分组构建拓扑（唯一真源：M2M node.node_groups 数组）
                const nodesByGroup = {};
                cdnNodes.forEach((node) => {
                    const groups = Array.isArray(node.node_groups) ? node.node_groups : [];
                    if (groups.length === 0) {
                        if (!nodesByGroup.ungrouped) nodesByGroup.ungrouped = [];
                        nodesByGroup.ungrouped.push(node);
                    } else {
                        // 一个节点可归属多个分组，逐组挂载
                        groups.forEach((g) => {
                            const gid = g.id;
                            if (!nodesByGroup[gid]) nodesByGroup[gid] = [];
                            nodesByGroup[gid].push(node);
                        });
                    }
                });

                // 创建节点分组
                nodeGroups.forEach((group) => {
                    const groupNodeId = `group-${group.id}`;
                    const groupNodes = nodesByGroup[group.id] || [];
                    const l2Nodes = groupNodes.filter((n) => n.is_l2);
                    const l1Nodes = groupNodes.filter((n) => !n.is_l2);

                    flowNodes.push({
                        id: groupNodeId,
                        type: 'nodeGroup',
                        data: {
                            label: group.name,
                            region: group.region,
                            l2Count: l2Nodes.length,
                            l1Count: l1Nodes.length,
                            raw: group,
                        },
                        position: { x: 0, y: 0 },
                        width: 200,
                        height: 90,
                    });

                    // 创建L2节点（id 带 group 前缀，避免多分组节点 id 冲突）
                    l2Nodes.forEach((node) => {
                        const l2NodeId = `l2-${group.id}-${node.id}`;
                        flowNodes.push({
                            id: l2NodeId,
                            type: 'l2Node',
                            data: {
                                label: node.name,
                                ip: node.ip,
                                status: node.status,
                                l2Port: node.l2_port,
                                raw: node,
                                onClick: handleNodeClick,
                            },
                            position: { x: 0, y: 0 },
                            width: 180,
                            height: 80,
                        });

                        // 分组 -> L2 边
                        flowEdges.push({
                            id: `edge-${groupNodeId}-${l2NodeId}`,
                            source: groupNodeId,
                            target: l2NodeId,
                            type: 'smoothstep',
                            animated: false,
                            style: { stroke: '#667eea', strokeWidth: 2 },
                            markerEnd: { type: MarkerType.ArrowClosed, color: '#667eea' },
                        });
                    });

                    // 创建L1节点
                    l1Nodes.forEach((node) => {
                        const l1NodeId = `l1-${group.id}-${node.id}`;
                        flowNodes.push({
                            id: l1NodeId,
                            type: 'l1Node',
                            data: {
                                label: node.name,
                                ip: node.ip,
                                status: node.status,
                                raw: node,
                                onClick: handleNodeClick,
                            },
                            position: { x: 0, y: 0 },
                            width: 160,
                            height: 70,
                        });

                        // 如果有L2节点，L1连接到L2；否则连接到分组
                        if (l2Nodes.length > 0) {
                            // 连接到第一个L2节点（简化显示）
                            const firstL2 = l2Nodes[0];
                            flowEdges.push({
                                id: `edge-l2-${group.id}-${firstL2.id}-l1-${node.id}`,
                                source: `l2-${group.id}-${firstL2.id}`,
                                target: l1NodeId,
                                type: 'smoothstep',
                                animated: true,
                                style: { stroke: '#eb3349', strokeWidth: 1.5 },
                                markerEnd: { type: MarkerType.ArrowClosed, color: '#eb3349' },
                            });
                        } else {
                            flowEdges.push({
                                id: `edge-${groupNodeId}-${l1NodeId}`,
                                source: groupNodeId,
                                target: l1NodeId,
                                type: 'smoothstep',
                                animated: false,
                                style: { stroke: '#667eea', strokeWidth: 1.5 },
                                markerEnd: { type: MarkerType.ArrowClosed, color: '#667eea' },
                            });
                        }
                    });
                });

                // 处理未分组的节点
                const ungroupedNodes = nodesByGroup['ungrouped'] || [];
                if (ungroupedNodes.length > 0) {
                    const ungroupedGroupId = 'group-ungrouped';
                    flowNodes.push({
                        id: ungroupedGroupId,
                        type: 'nodeGroup',
                        data: {
                            label: '未分组',
                            region: '-',
                            l2Count: ungroupedNodes.filter((n) => n.is_l2).length,
                            l1Count: ungroupedNodes.filter((n) => !n.is_l2).length,
                        },
                        position: { x: 0, y: 0 },
                        width: 200,
                        height: 90,
                    });

                    ungroupedNodes.forEach((node) => {
                        const nodeId = `ungrouped-${node.id}`;
                        flowNodes.push({
                            id: nodeId,
                            type: 'ungroupedNode',
                            data: {
                                label: node.name,
                                ip: node.ip,
                                status: node.status,
                                raw: node,
                                onClick: handleNodeClick,
                            },
                            position: { x: 0, y: 0 },
                            width: 160,
                            height: 60,
                        });

                        flowEdges.push({
                            id: `edge-${ungroupedGroupId}-${nodeId}`,
                            source: ungroupedGroupId,
                            target: nodeId,
                            type: 'smoothstep',
                            animated: false,
                            style: { stroke: '#636e72', strokeWidth: 1.5, strokeDasharray: '5,5' },
                            markerEnd: { type: MarkerType.ArrowClosed, color: '#636e72' },
                        });
                    });
                }
            } else {
                // CDN线路视图
                cdnGroups.forEach((group) => {
                    const groupNodeId = `cdnGroup-${group.id}`;
                    const groupLines = cdnLines.filter((l) => l.group_id === group.id);

                    flowNodes.push({
                        id: groupNodeId,
                        type: 'nodeGroup',
                        data: {
                            label: group.name,
                            region: group.cname_domain || '-',
                            l2Count: 0,
                            l1Count: groupLines.length,
                        },
                        position: { x: 0, y: 0 },
                        width: 200,
                        height: 90,
                    });

                    // 创建线路节点
                    groupLines.forEach((line) => {
                        const lineNodeId = `line-${line.id}`;
                        flowNodes.push({
                            id: lineNodeId,
                            type: 'l1Node',
                            data: {
                                label: line.name,
                                ip: line.line_type || '-',
                                status: line.status,
                                raw: line,
                                onClick: handleLineClick,
                            },
                            position: { x: 0, y: 0 },
                            width: 160,
                            height: 70,
                        });

                        flowEdges.push({
                            id: `edge-${groupNodeId}-${lineNodeId}`,
                            source: groupNodeId,
                            target: lineNodeId,
                            type: 'smoothstep',
                            animated: false,
                            style: { stroke: '#667eea', strokeWidth: 1.5 },
                            markerEnd: { type: MarkerType.ArrowClosed, color: '#667eea' },
                        });

                        // 如果线路有关联节点
                        if (line.nodes && line.nodes.length > 0) {
                            line.nodes.forEach((node) => {
                                const linkedNodeId = `lineNode-${line.id}-${node.id}`;
                                flowNodes.push({
                                    id: linkedNodeId,
                                    type: 'ungroupedNode',
                                    data: {
                                        label: node.name,
                                        ip: node.ip,
                                        status: node.status,
                                        raw: node,
                                        onClick: handleNodeClick,
                                    },
                                    position: { x: 0, y: 0 },
                                    width: 150,
                                    height: 60,
                                });

                                flowEdges.push({
                                    id: `edge-${lineNodeId}-${linkedNodeId}`,
                                    source: lineNodeId,
                                    target: linkedNodeId,
                                    type: 'smoothstep',
                                    animated: true,
                                    style: { stroke: '#11998e', strokeWidth: 1 },
                                    markerEnd: { type: MarkerType.ArrowClosed, color: '#11998e' },
                                });
                            });
                        }
                    });
                });
            }

            // 应用布局
            const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
                flowNodes,
                flowEdges,
                'TB'
            );

            setNodes(layoutedNodes);
            setEdges(layoutedEdges);
        },
        [setNodes, setEdges]
    );

    useEffect(() => {
        loadData();
    }, [loadData]);

    useEffect(() => {
        if (rawData.cdnNodes.length > 0) {
            buildTopology(
                rawData.nodeGroups,
                rawData.cdnNodes,
                rawData.cdnGroups,
                rawData.cdnLines,
                viewMode
            );
        }
    }, [viewMode, rawData, buildTopology]);

    const handleNodeClick = (nodeData) => {
        setSelectedNode({ type: 'node', data: nodeData });
        setDetailModalVisible(true);
    };

    const handleLineClick = (lineData) => {
        setSelectedNode({ type: 'line', data: lineData });
        setDetailModalVisible(true);
    };

    const toggleFullscreen = () => {
        setFullscreen(!fullscreen);
    };

    const containerStyle = fullscreen
        ? {
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1000,
              background: '#fff',
          }
        : { height: 'calc(100vh - 200px)', minHeight: 500 };

    return (
        <div>
            <Card
                title={
                    <Space>
                        <CloudOutlined />
                        <span>CDN 拓扑视图</span>
                        <Tag color="blue">{rawData.nodeGroups.length} 分组</Tag>
                        <Tag color="green">{rawData.cdnNodes.length} 节点</Tag>
                        <Tag color="red">
                            {rawData.cdnNodes.filter((n) => n.is_l2).length} L2
                        </Tag>
                        <Tooltip title="CDN_L2_TOPOLOGY_ENABLED=0：拓扑仅为配置意图，边缘全部直连回源">
                            <Tag color="orange">L2 未启用</Tag>
                        </Tooltip>
                        {viewMode === 'nodeGroups' && (
                            <Tooltip title="分组归属来自 M2M node_groups；L1→L2 连线为层级示意（配置意图），非实时回源路径">
                                <Tag color="default">L1→L2 示意</Tag>
                            </Tooltip>
                        )}
                    </Space>
                }
                extra={
                    <Space>
                        <Select
                            value={viewMode}
                            onChange={setViewMode}
                            style={{ width: 140 }}
                            options={[
                                { value: 'nodeGroups', label: '节点分组视图' },
                                { value: 'cdnLines', label: 'CDN线路视图' },
                            ]}
                        />
                        <Button icon={<ReloadOutlined />} onClick={loadData} loading={loading}>
                            刷新
                        </Button>
                        <Button
                            icon={fullscreen ? <CompressOutlined /> : <ExpandOutlined />}
                            onClick={toggleFullscreen}
                        >
                            {fullscreen ? '退出全屏' : '全屏'}
                        </Button>
                    </Space>
                }
                bodyStyle={{ padding: 0 }}
            >
                <div style={containerStyle}>
                    {loading ? (
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                height: '100%',
                            }}
                        >
                            <Spin size="large" tip="加载拓扑数据..." />
                        </div>
                    ) : (
                        <ReactFlow
                            nodes={nodes}
                            edges={edges}
                            onNodesChange={onNodesChange}
                            onEdgesChange={onEdgesChange}
                            nodeTypes={nodeTypes}
                            fitView
                            attributionPosition="bottom-left"
                        >
                            <Background color="#aaa" gap={16} />
                            <Controls />
                            <MiniMap
                                nodeColor={(node) => {
                                    switch (node.type) {
                                        case 'nodeGroup':
                                            return '#667eea';
                                        case 'l2Node':
                                            return '#eb3349';
                                        case 'l1Node':
                                            return '#11998e';
                                        case 'ungroupedNode':
                                            return '#636e72';
                                        default:
                                            return '#eee';
                                    }
                                }}
                                maskColor="rgba(0, 0, 0, 0.1)"
                            />
                        </ReactFlow>
                    )}
                </div>
            </Card>

            {/* 详情弹窗 */}
            <Modal
                title={
                    selectedNode?.type === 'node'
                        ? `节点详情: ${selectedNode?.data?.name}`
                        : `线路详情: ${selectedNode?.data?.name}`
                }
                open={detailModalVisible}
                onCancel={() => setDetailModalVisible(false)}
                footer={null}
                width={600}
            >
                {selectedNode?.type === 'node' && selectedNode?.data && (
                    <Descriptions bordered column={2}>
                        <Descriptions.Item label="ID">{selectedNode.data.id}</Descriptions.Item>
                        <Descriptions.Item label="名称">{selectedNode.data.name}</Descriptions.Item>
                        <Descriptions.Item label="IP地址">
                            {selectedNode.data.ip || '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="状态">
                            <Badge
                                status={
                                    selectedNode.data.status === 'active' ? 'success' : 'default'
                                }
                                text={selectedNode.data.status === 'active' ? '活跃' : '未激活'}
                            />
                        </Descriptions.Item>
                        <Descriptions.Item label="节点类型">
                            <Tag color={selectedNode.data.is_l2 ? 'red' : 'green'}>
                                {selectedNode.data.is_l2 ? 'L2 中枢' : 'L1 边缘'}
                            </Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="L2端口">
                            {selectedNode.data.l2_port || '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="地区">
                            <CountryFlag code={countryFromRegion(selectedNode.data.region)} />{selectedNode.data.region || '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="节点分组">
                            {Array.isArray(selectedNode.data.node_groups) && selectedNode.data.node_groups.length > 0
                                ? (
                                    <Space size={[0, 4]} wrap>
                                        {selectedNode.data.node_groups.map((g) => (
                                            <Tag key={g.id} icon={<ApartmentOutlined />} color="cyan">{g.name}</Tag>
                                        ))}
                                    </Space>
                                )
                                : '未分组'}
                        </Descriptions.Item>
                        <Descriptions.Item label="同步端口">
                            {selectedNode.data.sync_port || '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="最后心跳">
                            {selectedNode.data.last_seen
                                ? new Date(selectedNode.data.last_seen).toLocaleString()
                                : '-'}
                        </Descriptions.Item>
                    </Descriptions>
                )}
                {selectedNode?.type === 'line' && selectedNode?.data && (
                    <Descriptions bordered column={2}>
                        <Descriptions.Item label="ID">{selectedNode.data.id}</Descriptions.Item>
                        <Descriptions.Item label="名称">{selectedNode.data.name}</Descriptions.Item>
                        <Descriptions.Item label="线路类型">
                            {selectedNode.data.line_type || '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="状态">
                            <Badge
                                status={
                                    selectedNode.data.status === 'active' ? 'success' : 'default'
                                }
                                text={selectedNode.data.status === 'active' ? '活跃' : '未激活'}
                            />
                        </Descriptions.Item>
                        <Descriptions.Item label="地理大洲">
                            {selectedNode.data.geo_continent || '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="国家">
                            <CountryFlag code={selectedNode.data.geo_country} />{selectedNode.data.geo_country || '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="省份">
                            {selectedNode.data.geo_province || '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="ISP">
                            {selectedNode.data.isp || '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="优先级">
                            {selectedNode.data.priority || '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="权重">
                            {selectedNode.data.weight || '-'}
                        </Descriptions.Item>
                    </Descriptions>
                )}
            </Modal>
        </div>
    );
};

const TopologyGated = () => <L2Gate title="CDN L2 拓扑"><Topology /></L2Gate>;

export default TopologyGated;
